import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { AuthTokenResponse } from '@/api/customers/use-create-customer';
import { useCreateCustomer } from '@/api/customers/use-create-customer';
import { useEmailCheck } from '@/api/customers/use-email-check';
import { useLoginCustomer } from '@/api/customers/use-login-customer';
import { EntryForgotPassword } from '@/components/entry/forgot-password';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { signIn } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, ControlledInput, Input, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

const emailFormSchema = z.object({ email: z.string().email() });
type EmailFormType = z.infer<typeof emailFormSchema>;

const passwordFormSchema = z.object({
  password: z.string().min(8, 'Minimum 8 characters'),
});
type PasswordFormType = z.infer<typeof passwordFormSchema>;

const createPasswordFormSchema = z
  .object({
    password: z.string().min(8, 'Minimum 8 characters'),
    confirmPassword: z.string().min(8, 'Minimum 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match.',
    path: ['confirmPassword'],
  });
type CreatePasswordFormType = z.infer<typeof createPasswordFormSchema>;

interface EntryEmailFormProps {
  onBack: () => void;
  onDone: (setupComplete: boolean) => void;
}

type Step = 'email' | 'login' | 'create';

export function EntryEmailForm({ onBack, onDone }: EntryEmailFormProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const refCode = useSettingsStore((state) => state.refCode);
  const promoCode = useSettingsStore((state) => state.promoCode);

  const { mutate: checkEmail, isPending: isCheckingEmail } = useEmailCheck();

  const {
    mutate: loginCustomer,
    isPending: isLoggingIn,
    error: loginError,
  } = useLoginCustomer();

  const {
    mutate: createCustomer,
    isPending: isCreatingCustomer,
    error: createError,
  } = useCreateCustomer();

  const {
    control: emailControl,
    handleSubmit: handleEmailFormSubmit,
    formState: { isValid: isEmailValid },
  } = useForm<EmailFormType>({ resolver: zodResolver(emailFormSchema) });

  const {
    control: loginControl,
    handleSubmit: handleLoginFormSubmit,
    formState: { isValid: isLoginValid },
  } = useForm<PasswordFormType>({ resolver: zodResolver(passwordFormSchema) });

  const {
    control: createControl,
    handleSubmit: handleCreateFormSubmit,
    formState: { isValid: isCreateValid },
  } = useForm<CreatePasswordFormType>({
    resolver: zodResolver(createPasswordFormSchema),
  });

  const handleEmailSubmit = (data: EmailFormType) => {
    checkEmail(
      { email: data.email },
      {
        onSuccess: ({ data: { exists } }) => {
          setEmail(data.email);
          setStep(exists ? 'login' : 'create');
        },
        onError: (e) => showErrorMessage(e.message),
      }
    );
  };

  const handleLoginSubmit = (data: PasswordFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign In Email',
    });

    loginCustomer(
      { email, password: data.password },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign In Successful');
          signIn(respData.data.token);
          onDone(respData.data.setupComplete);
        },
        onError: () => mixpanelService.track('Sign In Failed'),
      }
    );
  };

  const handleCreateSubmit = (data: CreatePasswordFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign Up Email',
    });

    createCustomer(
      { email, password: data.password, referralCode: refCode, promoCode },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign Up Successful');
          signIn(respData.data.token);
          onDone(respData.data.setupComplete);
        },
        onError: () => mixpanelService.track('Sign Up Failed'),
      }
    );
  };

  const handleBack = () => {
    if (step === 'email') {
      onBack();
      return;
    }

    setStep('email');
  };

  if (showForgotPassword) {
    return (
      <EntryForgotPassword
        email={email}
        onBack={() => setShowForgotPassword(false)}
      />
    );
  }

  if (isLoggingIn || isCreatingCustomer) {
    return (
      <View style={styles.loaderContainer}>
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  if (step === 'login') {
    return (
      <View style={styles.card}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={handleBack}
        />

        <View style={styles.titleContainer}>
          <Text tx="entry.emailForm.loginTitle" type="title1" />
        </View>

        <View style={styles.inputsContainer}>
          <Input label="Email" value={email} disabled autoComplete="username" />

          <ControlledInput
            control={loginControl}
            name="password"
            label="Password"
            secureTextEntry
            autoComplete="current-password"
            onSubmitEditing={handleLoginFormSubmit(handleLoginSubmit)}
          />
        </View>

        <View style={styles.buttonContainer}>
          <Button
            tx="entry.emailForm.logIn"
            type="primary"
            onPress={handleLoginFormSubmit(handleLoginSubmit)}
            disabled={!isLoginValid}
          />

          {loginError ? (
            <Text type="bodyRegular" style={{ color: theme.colors.error }}>
              {loginError.message}
            </Text>
          ) : null}

          <Button
            tx="entry.emailForm.forgotPassword"
            type="ghost"
            onPress={() => setShowForgotPassword(true)}
          />
        </View>
      </View>
    );
  }

  if (step === 'create') {
    return (
      <View style={styles.card}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={handleBack}
        />

        <View style={styles.titleContainer}>
          <Text tx="entry.emailForm.createTitle" type="title1" />
          <Text
            tx="entry.emailForm.createSubtitle"
            type="bodyRegular"
            style={styles.cardSubtitle}
          />
        </View>

        <View style={styles.inputsContainer}>
          <Input label="Email" value={email} disabled autoComplete="username" />

          <ControlledInput
            control={createControl}
            name="password"
            label="Password"
            secureTextEntry
            autoComplete="new-password"
            onSubmitEditing={handleCreateFormSubmit(handleCreateSubmit)}
          />

          <ControlledInput
            control={createControl}
            name="confirmPassword"
            label="Confirm password"
            secureTextEntry
            autoComplete="new-password"
            onSubmitEditing={handleCreateFormSubmit(handleCreateSubmit)}
          />
        </View>

        <View style={styles.buttonContainer}>
          <Button
            tx="entry.emailForm.continue"
            type="primary"
            onPress={handleCreateFormSubmit(handleCreateSubmit)}
            disabled={!isCreateValid}
          />

          {createError ? (
            <Text type="bodyRegular" style={{ color: theme.colors.error }}>
              {createError.message}
            </Text>
          ) : null}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <NavigationButton
        type={NavigationButtonTypes.back_with_text}
        onPress={handleBack}
      />

      <View style={styles.titleContainer}>
        <Text tx="entry.emailForm.title" type="title1" />
      </View>

      <View style={styles.inputsContainer}>
        <ControlledInput
          control={emailControl}
          name="email"
          label="Email"
          autoComplete="email"
          spellCheck={false}
          onSubmitEditing={handleEmailFormSubmit(handleEmailSubmit)}
        />
      </View>

      <View style={styles.buttonContainer}>
        <Button
          tx="entry.emailForm.continue"
          type="primary"
          onPress={handleEmailFormSubmit(handleEmailSubmit)}
          disabled={!isEmailValid}
          loading={isCheckingEmail}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  loaderContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    paddingHorizontal: 18,
    gap: 16,
  },
  titleContainer: {
    gap: 8,
    marginTop: 8,
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    gap: 16,
    alignItems: 'center',
  },
}));
